import { ScrollState } from '@legendapp/list';
import React, { createContext, useContext } from 'react';
import type { SharedValue } from 'react-native-reanimated';

export type PlayerListTemplateListRef = {
  scrollToIndex?: (params: {
    index: number;
    animated?: boolean;
    viewOffset?: number;
    viewPosition?: number;
  }) => void;
  /** Prefer for “scroll to top”: works when row 0 is taller than the viewport (scrollToIndex cannot). */
  scrollToOffset?: (params: { offset: number; animated?: boolean }) => void;
  /** LegendList only; missing on GridListMasonry (FlashList). */
  getState?(): ScrollState;
} | null;

export type PlayerListTemplateContextValue<T extends { id: string } = { id: string }> = {
  /** The list logic hook result (data + pagination + search + sort + filter). */
  listLogic: player.ListPlayerLogic<T>;

  /** Shared scroll offset driving the mobile toolbar collapse animation. */
  scrollY: SharedValue<number>;
  /** Measured height of the mobile action button (drives toolbar translate). */
  buttonCreateProfileHeight: SharedValue<number>;
  /** JS-side mirror of the measured action button height (for list top spacer). */
  headerActionMeasuredHeight: number;
  setHeaderActionMeasuredHeight: (height: number) => void;

  /** True when the Toolbar mounts an ActionButton slot with actual content. */
  hasActionButton: boolean;
  setHasActionButton: (value: boolean) => void;

  /** Ref exposed by `List` so other slots (e.g. Pagination) could trigger scrolls. */
  listRef: React.MutableRefObject<PlayerListTemplateListRef>;

  /** When `true`, toolbar/action button skip the hide-on-scroll animation. */
  skipHideActionButtonOnScroll: boolean;

  /** Default search placeholder used by `SearchBar` when no prop is provided. */
  searchPlaceholder: string;
};

const PlayerListTemplateContext = createContext<PlayerListTemplateContextValue | null>(null);

export const PlayerListTemplateProvider = PlayerListTemplateContext.Provider;

export function usePlayerListTemplate<
  T extends { id: string } = player.PlayerLookupData,
>(): PlayerListTemplateContextValue<T> {
  const ctx = useContext(PlayerListTemplateContext);
  if (!ctx) {
    throw new Error(
      '[PlayerListTemplateV2] compound components must be rendered inside <PlayerListTemplateV2.Root>',
    );
  }
  return ctx as unknown as PlayerListTemplateContextValue<T>;
}
